import { format } from 'date-fns'
import { useMemo } from 'react'

import { LINE_CHART_EMPTY_DATA } from 'src/components/analytics/charts/ComparisonLineChart'
import { ComparisonLineChartDataType } from 'src/components/analytics/types/comparison-line-chart'
import { useChartData } from 'src/components/overview/useChartData'
import { formatDateRange } from 'src/components/sso-analytics/charts/TotalIdentificationRate/parser'
import { useAnalytics } from 'src/lib/contexts/AnalyticsContext'
import { formatNumberToTwoDecimals, percentageWithFixedDecmials } from 'src/lib/utils/analytics'
import { getTimeIncrement, sortDataByDate } from '../common/utils'
import { WidgetId } from 'src/lib/contexts/AnalyticsContext/types'

const EMPTY_DATA = {
  ...LINE_CHART_EMPTY_DATA,
  total: 0,
  percentageChange: 0,
  currentDateRangeLegend: '',
  previousDateRangeLegend: ''
}

export const useOrderData = ({ dataKey }: { dataKey: 'order_completed_count' | 'total_gmv' | 'aov' }) => {
  const {
    getters: { selectedTimeScale, refreshKey }
  } = useAnalytics()
  const { ref, isLoading, isError, data } = useChartData({
    chartConfig: {
      id: WidgetId.orders,
      dependencies: {
        endpoints: ['/shopflo-orders-timescale', '/order-details']
      }
    },
    refreshKey
  })

  const orderData: ComparisonLineChartDataType & {
    total: number
    percentageChange: number
    currentDateRangeLegend: string
    previousDateRangeLegend: string
  } = useMemo(() => {
    const rawData = data[0]?.data?.result?.widgetData?.data?.[0]?.data
    const rawComparedData = data[0]?.data?.result?.widgetData?.compared_data?.[0]?.data

    const rawDataSummary = data[0]?.data?.result?.widgetData?.data?.[0]?.summary
    const rawComparedDataSummary = data[0]?.data?.result?.widgetData?.compared_data?.[0]?.summary

    const averageOrderValueData = formatNumberToTwoDecimals(
      data[1]?.data?.result?.widgetData?.data?.average_order_value || 0
    )
    const averageOrderValueComparedData = formatNumberToTwoDecimals(
      data[1]?.data?.result?.widgetData?.compared_data?.average_order_value || 0
    )

    if (rawData && rawData.every((item: any) => item[dataKey] === 0)) {
      if (rawComparedData && rawComparedData.every((item: any) => item[dataKey] === 0)) {
        return EMPTY_DATA
      }
      return EMPTY_DATA
    }

    if (!rawData || rawData.length === 0) {
      return EMPTY_DATA
    }

    const currentData = rawData.sort(sortDataByDate)
    let previousData: any[] = []
    if (rawComparedData) {
      previousData = rawComparedData.sort(sortDataByDate)
    }

    const currentRange =
      new Date(currentData[currentData.length - 1].date).getTime() - new Date(currentData[0].date).getTime()
    let previousRange = 0
    if (previousData.length > 0) {
      previousRange =
        new Date(previousData[previousData.length - 1].date).getTime() - new Date(previousData[0].date).getTime()
    }

    const isCurrentPrimary = currentRange >= previousRange
    const primaryDataset = isCurrentPrimary ? currentData : previousData

    const dataIterator = (data: any[]) => {
      return data.map((item, index) => ({
        date: new Date(item.date),
        displayDate: new Date(new Date(primaryDataset[0].date).getTime() + index * getTimeIncrement(selectedTimeScale)),
        rate: item[dataKey],
        actualDate: new Date(item.date)
      }))
    }

    const mappedCurrentData = dataIterator(currentData)
    const mappedPreviousData = dataIterator(previousData)

    const total = rawDataSummary?.[dataKey] || 0
    const previousTotal = rawComparedDataSummary?.[dataKey] || 0
    const percentageChange = percentageWithFixedDecmials(total, previousTotal)

    const currentDateRange = {
      from: new Date(currentData[0].date),
      to: new Date(currentData[currentData.length - 1].date)
    }

    const previousDateRange =
      previousData.length > 0
        ? {
            from: new Date(previousData[0].date),
            to: new Date(previousData[previousData.length - 1].date)
          }
        : null

    const getTotal = () => {
      if (dataKey === 'aov') {
        return averageOrderValueData
      }
      return total
    }

    const getPercentageChange = () => {
      if (dataKey === 'aov') {
        return percentageWithFixedDecmials(averageOrderValueData, averageOrderValueComparedData)
      }
      return percentageChange
    }

    return {
      currentWeekData: mappedCurrentData,
      previousWeekData: mappedPreviousData,
      primaryDataset: isCurrentPrimary ? mappedCurrentData : mappedPreviousData,
      secondaryDataset: isCurrentPrimary ? mappedPreviousData : mappedCurrentData,
      isCurrentPrimary,
      currentDateRange,
      previousDateRange,
      // extra
      total: getTotal(),
      percentageChange: getPercentageChange(),
      currentDateRangeLegend:
        mappedCurrentData.length === 1
          ? format(currentDateRange.from, 'MMM dd, yyyy')
          : formatDateRange(currentDateRange.from, currentDateRange.to, selectedTimeScale),
      previousDateRangeLegend: previousDateRange
        ? formatDateRange(previousDateRange.from, previousDateRange.to, selectedTimeScale)
        : ''
    }
  }, [data, selectedTimeScale, dataKey])

  return {
    orderData,
    isLoading,
    ref,
    isError,
    selectedTimeScale
  }
}
